/**
 * Shared score color utility.
 *
 * Consolidates 10+ duplicate getScoreColor functions across the codebase
 * into a single source of truth with multiple output variants.
 *
 * Standard 3-tier thresholds:
 *   >= 80 -> emerald (green)
 *   >= 60 -> amber (yellow/orange)
 *   <  60 -> red
 */

type ScoreInput = number | null | undefined;

/**
 * Returns Tailwind classes for score coloring based on variant.
 *
 * Variants:
 * - 'badge'  -> 'bg-{color}-100 text-{color}-700' (for badges/cells)
 * - 'text'   -> 'text-{color}-600' (for text-only coloring)
 * - 'ring'   -> hex color string (for Recharts/SVG)
 * - 'bg'     -> 'bg-{color}-50' (for light background tinting)
 */
export function getScoreColor(score: ScoreInput, variant: 'badge'): string;
export function getScoreColor(score: ScoreInput, variant: 'text'): string;
export function getScoreColor(score: ScoreInput, variant: 'ring'): string;
export function getScoreColor(score: ScoreInput, variant: 'bg'): string;
export function getScoreColor(score: ScoreInput, variant: 'badge' | 'text' | 'ring' | 'bg'): string {
  if (score === null || score === undefined) {
    switch (variant) {
      case 'badge': return 'bg-gray-100 text-gray-400';
      case 'text':  return 'text-gray-400';
      case 'ring':  return '#9ca3af'; // gray-400
      case 'bg':    return 'bg-gray-50';
    }
  }

  if (score >= 80) {
    switch (variant) {
      case 'badge': return 'bg-emerald-100 text-emerald-700';
      case 'text':  return 'text-emerald-600';
      case 'ring':  return '#059669'; // emerald-600
      case 'bg':    return 'bg-emerald-50';
    }
  }

  if (score >= 60) {
    switch (variant) {
      case 'badge': return 'bg-amber-100 text-amber-700';
      case 'text':  return 'text-amber-600';
      case 'ring':  return '#d97706'; // amber-600
      case 'bg':    return 'bg-amber-50';
    }
  }

  switch (variant) {
    case 'badge': return 'bg-red-100 text-red-700';
    case 'text':  return 'text-red-600';
    case 'ring':  return '#dc2626'; // red-600
    case 'bg':    return 'bg-red-50';
  }
}

/**
 * Returns bg + text + border classes for score badges.
 * Used in GroupedMatrixView and similar components that need bordered badges.
 */
export function getScoreBadgeColor(score: ScoreInput): string {
  if (score === null || score === undefined) return 'bg-gray-100 text-gray-500 border-gray-200';
  if (score >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (score >= 60) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

/**
 * Returns bg + border classes for score containers (ReviewStepV2 pattern).
 * Includes border color for use with `border` class.
 */
export function getScoreBgWithBorder(score: ScoreInput): string {
  if (score === null || score === undefined) return 'bg-gray-50 border-gray-200';
  if (score >= 80) return 'bg-emerald-50 border-emerald-200';
  if (score >= 60) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
}

/**
 * Returns an object with ring/bg/text classes for score gauges.
 * Used in TriggerDetailPage ScoreGauge component.
 */
export function getScoreColorSet(score: ScoreInput): { ring: string; bg: string; text: string } {
  if (score === null || score === undefined) {
    return { ring: 'text-gray-400', bg: 'bg-gray-50', text: 'text-gray-400' };
  }
  if (score >= 80) return { ring: 'text-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' };
  if (score >= 60) return { ring: 'text-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' };
  return { ring: 'text-red-500', bg: 'bg-red-50', text: 'text-red-700' };
}
